/**
 * Контракт HTTP API. Один файл — одна правда для сервера і клієнта.
 *
 * Незмінне правило: клієнт НЕ МОЖЕ надіслати агрегат.
 * Немає жодного ендпоїнта, який приймав би «бали», «серію» чи «статистику».
 * Клієнт надсилає тільки факти (спроби), а всі похідні значення рахує сервер.
 * Саме цим нова система відрізняється від старої, де POST /api/scores
 * з порожнім кешем стирав увесь прогрес.
 */

import { z } from 'zod';
import { SUBJECT_IDS, SESSION_MODES, ITEM_FORMATS, USER_ROLES } from './domain.js';
import { DIFFICULTY_MODES, LEDGER_KINDS } from './rewards.js';

// ─────────────────────────────────────────────────────────────────────────────
// Примітиви
// ─────────────────────────────────────────────────────────────────────────────

export const zUuid = z.string().uuid();
export const zSubjectId = z.enum(SUBJECT_IDS);
export const zSessionMode = z.enum(SESSION_MODES);
export const zDifficultyMode = z.enum(DIFFICULTY_MODES);
export const zItemFormat = z.enum(ITEM_FORMATS);
export const zLedgerKind = z.enum(LEDGER_KINDS);
export const zGrade = z.number().int().min(1).max(7);
export const zIsoDate = z.string().datetime({ offset: true });

/** Відповідь дитини. Форма залежить від формату питання. */
export const zResponse = z.union([
  z.object({ kind: z.literal('choice'), choiceId: z.string().max(64) }),
  z.object({ kind: z.literal('choices'), choiceIds: z.array(z.string().max(64)).max(12) }),
  z.object({ kind: z.literal('numeric'), value: z.number().finite() }),
  z.object({ kind: z.literal('text'), value: z.string().max(300) }),
  z.object({ kind: z.literal('order'), sequence: z.array(z.string().max(64)).max(12) }),
  z.object({
    kind: z.literal('match'),
    pairs: z.array(z.tuple([z.string().max(64), z.string().max(64)])).max(12),
  }),
  /** Час вийшов — дитина не відповіла. Зараховується як спроба, але не як помилка знання. */
  z.object({ kind: z.literal('timeout') }),
]);
export type ResponsePayload = z.infer<typeof zResponse>;

// ─────────────────────────────────────────────────────────────────────────────
// Автентифікація
// ─────────────────────────────────────────────────────────────────────────────

export const zLoginChild = z.object({
  learnerId: zUuid,
  /** PIN дитини — 4 цифри. Короткий навмисно: це не банк, а домашній застосунок. */
  pin: z.string().regex(/^\d{4,6}$/),
  /** Стабільний id пристрою — щоб не питати PIN щоразу на довіреному телефоні. */
  deviceId: z.string().min(8).max(64),
});

/**
 * Єдиний вхід у застосунок: одне поле PIN.
 * Хто саме зайшов — вирішує сервер за самим PIN, тому дитині не треба
 * спершу шукати себе в списку. Профілі назовні не публікуються взагалі.
 *
 * Сюди заходять діти (4–6 цифр) і глядач (`viewer`, рівно 6 цифр — його
 * заводить `set-viewer`). Батько сюди більше не потрапляє: його PIN відкривав гаманець і вивід балів, стоячи в тому
 * самому полі, що й дитячий, — і бот, який перебирав цифри, з однаковим
 * успіхом влучав у будь-який із трьох кодів.
 */
export const zLoginPin = z.object({
  pin: z.string().regex(/^\d{4,6}$/),
  deviceId: z.string().min(8).max(64),
});

export const zLoginParent = z.object({
  email: z.string().email().max(120),
  password: z.string().min(8).max(200),
  deviceId: z.string().min(8).max(64),
});

export const zMeResponse = z.object({
  user: z.object({
    id: zUuid,
    role: z.enum(USER_ROLES),
    displayName: z.string(),
    avatarKey: z.string(),
    learnerId: zUuid.nullable(),
  }),
  /** Для батька — список дітей, якими він керує. */
  learners: z.array(
    z.object({
      id: zUuid,
      displayName: z.string(),
      avatarKey: z.string(),
      grade: zGrade,
    }),
  ),
  /** Коли протухне сесія. Клієнт оновлює її завчасно. */
  sessionExpiresAt: zIsoDate,
});
export type MeResponse = z.infer<typeof zMeResponse>;

/**
 * Passkey акаунта (Face ID / відбиток / апаратний ключ) — і в батька, і в дитини.
 *
 * Самі опції та відповіді автентифікатора тут НЕ описані навмисно: їх форму
 * задає стандарт WebAuthn, вона проходить крізь нас незміненою і перевіряється
 * криптографічно на сервері. Описувати її вдруге своїми словами — множити
 * джерела правди.
 *
 * `label` рахує сервер із транспортів ключа: окремого поля «назва» в базі
 * немає, бо нема кому його заповнювати.
 */
export const zPasskey = z.object({
  id: zUuid,
  label: z.string(),
  createdAt: zIsoDate,
  lastUsedAt: zIsoDate.nullable(),
});
export type Passkey = z.infer<typeof zPasskey>;

export const zPasskeyList = z.object({ passkeys: z.array(zPasskey) });
export type PasskeyList = z.infer<typeof zPasskeyList>;

/**
 * Підтвердження входу за passkey. `response` — відповідь автентифікатора
 * як є; `deviceId` потрібен рівно для того ж, що й у решті входів.
 */
export const zPasskeyLogin = z.object({
  response: z.unknown(),
  deviceId: z.string().min(8).max(64),
});

// ─────────────────────────────────────────────────────────────────────────────
// Сесія завдань
// ─────────────────────────────────────────────────────────────────────────────

export const zStartSession = z.object({
  learnerId: zUuid,
  mode: zSessionMode.default('daily'),
  difficultyMode: zDifficultyMode,
  /** Скільки питань хоче дитина. Сервер обмежує згори за батьківськими лімітами. */
  length: z.number().int().min(5).max(50).default(15),
  /** Для practice-режиму — конкретний предмет. */
  subject: zSubjectId.optional(),
  /** Id, згенерований клієнтом, щоб повторний запит не створив другу сесію. */
  idempotencyKey: zUuid,
});
export type StartSessionInput = z.infer<typeof zStartSession>;

export const zItemForLearner = z.object({
  id: z.string().max(200),
  skillId: z.string().max(120),
  skillTitle: z.string(),
  subject: zSubjectId,
  format: zItemFormat,
  prompt: z.string(),
  passage: z.string().optional(),
  choices: z
    .array(z.object({ id: z.string(), text: z.string(), image: z.string().optional() }))
    .optional(),
  /** Смуга складності визначає ставку: easy = 1 бал, hard = 2 бали. */
  band: z.enum(['easy', 'hard']),
  coinValue: z.number().int().min(0).max(10),
  hintCount: z.number().int().min(0).max(4),
  timeLimitSec: z.number().int().min(5).max(300),
  unit: z.string().optional(),
  /** Порядковий номер у сесії, з 1. */
  position: z.number().int().min(1),
});

/**
 * Стан «чи можна зараз грати». Рахується з фактичних спроб на сервері,
 * тому не залежить від годинника пристрою і переживає перезавантаження.
 */
export const zPlayWindow = z.object({
  /**
   * Доки триває обовʼязкова перерва. `null` — перерви немає, грати можна.
   */
  breakUntil: zIsoDate.nullable(),
  /**
   * Коли поточний відрізок гри впреться в ліміт і почнеться перерва.
   * `null` — ліміту немає (батько вимкнув) або відрізок ще не почався.
   */
  playUntil: zIsoDate.nullable(),
  /** Скільки хвилин поспіль можна грати. 0 — без обмеження. */
  sessionMinutes: z.number().int(),
  /** Скільки хвилин триває перерва. 0 — без перерв. */
  breakMinutes: z.number().int(),
});
export type PlayWindow = z.infer<typeof zPlayWindow>;

export const zPlayWindowResponse = zPlayWindow.extend({ learnerId: zUuid });
export type PlayWindowResponse = z.infer<typeof zPlayWindowResponse>;

export const zSessionResponse = z.object({
  sessionId: zUuid,
  mode: zSessionMode,
  difficultyMode: zDifficultyMode,
  plannedCount: z.number().int(),
  blueprint: z.array(z.object({ subject: zSubjectId, count: z.number().int() })),
  /**
   * УСІ питання сесії видаються одразу — щоб дитина могла дограти офлайн,
   * якщо зникне інтернет. Правильних відповідей тут немає.
   */
  items: z.array(zItemForLearner),
  /** Скільки максимум можна заробити в цій сесії. Показується на старті. */
  maxCoins: z.number().int(),
  /**
   * Стан перерви на момент видачі сесії. Довжина самої сесії вже підрізана
   * під залишок відрізка, але клієнт має знати, коли саме йти відпочивати.
   */
  playWindow: zPlayWindow,
});
export type SessionResponse = z.infer<typeof zSessionResponse>;

// ─────────────────────────────────────────────────────────────────────────────
// Спроби — головний ендпоїнт системи
// ─────────────────────────────────────────────────────────────────────────────

export const zAttemptInput = z.object({
  /**
   * UUIDv7, згенерований на клієнті ДО відправки. Робить операцію
   * ідемпотентною: скільки б разів не повторили запит, спроба запишеться один раз.
   */
  id: zUuid,
  sessionId: zUuid,
  itemId: z.string().max(200),
  response: zResponse,
  responseMs: z.number().int().min(0).max(600_000),
  hintsUsed: z.number().int().min(0).max(4),
  clientTs: zIsoDate,
});
export type AttemptInput = z.infer<typeof zAttemptInput>;

/** Спроби надсилаються пачками: і онлайн (по одній), і при синхронізації офлайн-черги. */
export const zAttemptBatch = z.object({
  attempts: z.array(zAttemptInput).min(1).max(100),
});

export const zAttemptResult = z.object({
  attemptId: zUuid,
  isCorrect: z.boolean(),
  /** Правильна відповідь — приходить ТІЛЬКИ у відповідь на спробу, не раніше. */
  correctAnswer: z.string(),
  explanation: z.string(),
  coinsAwarded: z.number().int(),
  coinReasons: z.array(z.string()),
  xpAwarded: z.number().int(),
  /** Новий стан навички — щоб одразу показати рух шкали. */
  skillId: z.string(),
  masteryBefore: z.number(),
  masteryAfter: z.number(),
  /** Серія правильних відповідей у сесії. */
  streak: z.number().int(),
  /** Розблоковані досягнення. */
  unlockedAchievements: z.array(z.string()),
});
export type AttemptResult = z.infer<typeof zAttemptResult>;

/**
 * Поштучний вирок: чому саме ЦЯ спроба не зарахована.
 *
 * Досі вирок був один на всю пачку — ним був HTTP-статус. Одна спроба з
 * офлайн-черги, яку сервер не міг прийняти, робила 4xx усьому запиту: чесні
 * відповіді, оброблені до неї, вже були оплачені, але клієнт бачив відмову,
 * а ті, що стояли за нею, сервер не бачив ніколи. Оскільки черга віддає
 * найстаріші рядки першими, отруйна спроба потрапляла в КОЖНУ наступну
 * пачку — затор назавжди.
 *
 * Тому вирок переїхав у тіло відповіді, поруч із прийнятими.
 *
 * Дві осі тут РІЗНІ, і змішувати їх не можна:
 *  · `code`/`message` кажуть ДИТИНІ, що сталося;
 *  · `retryable`/`retryAt` кажуть ЧЕРЗІ, що робити з рядком.
 * Саме їх злиття і було вихідним дефектом: клієнт виводив долю відповіді
 * з HTTP-статусу, тобто вгадував.
 */
export const zAttemptRejection = z.object({
  attemptId: zUuid,
  /**
   * Код із `API_ERROR_CODES` — той самий, що прилетів би в тілі помилки.
   * Множина навмисно ВІДКРИТА (z.string, не z.enum): нову серверну обставину
   * додають кодом, а клієнт, який його ще не знає, покаже текст із `message`
   * і не впаде на розборі відповіді.
   */
  code: z.string(),
  /** Готовий текст дитині, українською. Клієнт нічого не вигадує. */
  message: z.string(),
  /**
   * Чи має сенс надіслати цю спробу ще раз. `false` — вирок остаточний
   * (час відповіді й склад сесії вже не зміняться), рядок іде в `rejected`:
   * видно людині, оборотно кнопкою «спробувати ще раз».
   */
  retryable: z.boolean(),
  /** Не раніше цього моменту повторювати. `null` — повторювати нема сенсу. */
  retryAt: zIsoDate.nullable(),
});
export type AttemptRejection = z.infer<typeof zAttemptRejection>;

export const zAttemptBatchResponse = z.object({
  /** Прийняті спроби. Форма НЕ змінилась — старий клієнт читає її як читав. */
  results: z.array(zAttemptResult),
  /**
   * Не зараховані спроби. Поле АДИТИВНЕ і з типовим порожнім значенням —
   * тому воно нічого не ламає в обидва боки: старий клієнт зріже незнайомий
   * ключ (і його черга нарешті зрушить, бо 4xx на всю пачку більше немає),
   * а новий клієнт зі старим сервером отримає порожній масив.
   *
   * Інваріант: `results.length + rejected.length` = довжині запиту,
   * і кожен `attemptId` рівно в одному з двох масивів.
   */
  rejected: z.array(zAttemptRejection).default([]),
  wallet: z.object({
    balance: z.number().int(),
    earnedToday: z.number().int(),
    dailyCap: z.number().int(),
  }),
  /**
   * Стан відрізка гри ПІСЛЯ цієї пачки.
   *
   * Потрібен саме тепер: перерва більше не приїжджає як 403 на весь запит,
   * тож без цього поля дитина дізнавалася б про неї лише з наступного
   * опитування `/api/play-window` — до пів хвилини відповідей у порожнечу.
   */
  playWindow: zPlayWindow.nullable().default(null),
});

// ─────────────────────────────────────────────────────────────────────────────
// Підказки
// ─────────────────────────────────────────────────────────────────────────────

export const zHintRequest = z.object({
  sessionId: zUuid,
  itemId: z.string().max(200),
  /** Індекс підказки з 0. Видаються послідовно. */
  index: z.number().int().min(0).max(3),
});
export const zHintResponse = z.object({
  text: z.string(),
  /** Чи лишилися ще підказки. */
  hasMore: z.boolean(),
  /** Скільки балів коштуватиме питання після цієї підказки. */
  coinValueAfter: z.number().int(),
});

// ─────────────────────────────────────────────────────────────────────────────
// Гаманець
// ─────────────────────────────────────────────────────────────────────────────

export const zWalletResponse = z.object({
  learnerId: zUuid,
  totalEarned: z.number().int(),
  totalPaidOut: z.number().int(),
  balance: z.number().int(),
  earnedToday: z.number().int(),
  earnedThisWeek: z.number().int(),
  /** Стеля заробітку за добу, балів. 0 = без ліміту. Це НЕ денна ціль. */
  dailyCap: z.number().int(),
  /**
   * Денна ціль у ПИТАННЯХ — те, що батько задав у кабінеті. 0 = ціль не задана.
   *
   * Ціль і стеля заробітку — різні речі, і саме їх плутанина колись показувала
   * дитині «Сьогодні 12/0» з порожньою смугою: смуга міряла стелю (дефолт 0 =
   * без ліміту), а підписана була ціллю.
   *
   * `default(0)` — щоб відповідь старішого сервера (телефон з кешем PWA
   * пережив деплой) не валила екран цілком: без числа просто немає смуги.
   */
  dailyGoalItems: z.number().int().min(0).default(0),
  /**
   * Скільки питань дитина вже пройшла сьогодні — правильно чи ні. Саме цим
   * міряється поступ до денної цілі: ціль у навчанні, а не в грошах.
   */
  answeredToday: z.number().int().min(0).default(0),
  recentEntries: z.array(
    z.object({
      id: zUuid,
      kind: zLedgerKind,
      amount: z.number().int(),
      reasons: z.array(z.string()),
      note: z.string().nullable(),
      createdAt: zIsoDate,
    }),
  ),
});

/**
 * Вивід балів, який ініціює САМА дитина.
 *
 * `mode: 'all'` не передає суму навмисно: баланс може змінитися між
 * відкриттям екрана і натисканням кнопки (черга якраз догнала відповіді),
 * і тоді клієнтське число було б застарілим. Скільки саме виводиться,
 * вирішує сервер у момент запису.
 */
export const zWithdrawInput = z.object({
  learnerId: zUuid,
  request: z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('all') }),
    z.object({ mode: z.literal('exact'), amount: z.number().int().min(1).max(1_000_000) }),
  ]),
  idempotencyKey: zUuid,
});
export type WithdrawInput = z.infer<typeof zWithdrawInput>;

/** Виплата готівкою. Тільки батько. Створює від'ємний запис у журналі. */
export const zPayoutInput = z.object({
  learnerId: zUuid,
  amount: z.number().int().min(1).max(100_000),
  note: z.string().max(300).optional(),
  idempotencyKey: zUuid,
});

// ─────────────────────────────────────────────────────────────────────────────
// Батьківський кабінет
// ─────────────────────────────────────────────────────────────────────────────

export const zProgressQuery = z.object({
  learnerId: zUuid,
  /** Період у днях для трендів. */
  days: z.number().int().min(1).max(365).default(30),
});

export const zSkillProgress = z.object({
  skillId: z.string(),
  title: z.string(),
  subject: zSubjectId,
  strand: z.string(),
  mastery: z.number(),
  elo: z.number(),
  attempts: z.number().int(),
  correct: z.number().int(),
  dueAt: zIsoDate.nullable(),
  lastAttemptAt: zIsoDate.nullable(),
  /** Тренд за період: додатний — росте. */
  trend: z.number(),
});

export const zProgressResponse = z.object({
  learner: z.object({
    id: zUuid,
    displayName: z.string(),
    grade: zGrade,
    avatarKey: z.string(),
  }),
  /** Зведення по предметах — саме тут видно, що математика провисає. */
  subjects: z.array(
    z.object({
      subject: zSubjectId,
      mastery: z.number(),
      attempts: z.number().int(),
      accuracy: z.number(),
      /** Скільки відсотків питань зараз спрямовується на цей предмет. */
      currentWeight: z.number(),
      isWeak: z.boolean(),
      trend: z.number(),
    }),
  ),
  skills: z.array(zSkillProgress),
  /** Навички, які системa вважає пріоритетними для підтягування. */
  focusSkills: z.array(z.string()),
  daily: z.array(
    z.object({
      date: z.string(),
      attempts: z.number().int(),
      correct: z.number().int(),
      minutes: z.number(),
      coins: z.number().int(),
    }),
  ),
  streakDays: z.number().int(),
  totalAttempts: z.number().int(),
});
export type ProgressResponse = z.infer<typeof zProgressResponse>;

/**
 * Малюнок, який не відмалювався на екрані.
 *
 * `SafeSvg` на зламаній розмітці повертає `null` — дитина бачить порожню рамку.
 * Раніше про це не дізнавався ніхто, і саме ця тиша дала попередній поломці
 * з малюнками прожити стільки, скільки вона прожила.
 */
export const zFigureFailureReport = z.object({
  itemId: z.string().min(1).max(200),
  /** Де стояв малюнок: умова чи картинка варіанта. */
  field: z.enum(['passage', 'choice']),
});
export type FigureFailureReport = z.infer<typeof zFigureFailureReport>;

export const zFigureFailuresResponse = z.object({
  items: z.array(
    z.object({
      itemId: z.string(),
      field: z.enum(['passage', 'choice']),
      skillId: z.string(),
      skillTitle: z.string(),
      /** Текст питання — щоб батько зрозумів, про що йдеться. */
      prompt: z.string(),
      generatorId: z.string().nullable(),
      timesSeen: z.number().int(),
      firstSeenAt: zIsoDate,
      lastSeenAt: zIsoDate,
    }),
  ),
});
export type FigureFailuresResponse = z.infer<typeof zFigureFailuresResponse>;

/** Помилки для роботи над ними: що саме дитина плутає. */
export const zMistakesResponse = z.object({
  items: z.array(
    z.object({
      itemId: z.string(),
      skillId: z.string(),
      skillTitle: z.string(),
      subject: zSubjectId,
      prompt: z.string(),
      /** Малюнок-умова. Без нього «Продовж ряд» батькові нічого не каже. */
      passage: z.string().nullable(),
      correctAnswer: z.string(),
      givenAnswer: z.string(),
      explanation: z.string(),
      timesWrong: z.number().int(),
      lastWrongAt: zIsoDate,
      /** Чи вже виправлено пізніше. */
      resolved: z.boolean(),
    }),
  ),
});

export const zControlsInput = z.object({
  learnerId: zUuid,
  schedule: z
    .array(
      z.object({
        weekday: z.number().int().min(0).max(6),
        startMinute: z.number().int().min(0).max(1440),
        endMinute: z.number().int().min(0).max(1440),
      }),
    )
    .max(21),
  /** 0 = без денного ліміту часу. */
  dailyMinutesLimit: z.number().int().min(0).max(600),
  /** 0 = без примусової перерви. */
  sessionMinutes: z.number().int().min(0).max(120),
  breakMinutes: z.number().int().min(0).max(120),
  dailyGoalItems: z.number().int().min(0).max(200),
  /** 0 = без денного ліміту заробітку. */
  dailyCoinCap: z.number().int().min(0).max(1000),
  enabledSubjects: z.array(zSubjectId).min(1),
  subjectWeightOverride: z.record(zSubjectId, z.number().min(0).max(5)).optional(),
  grade: zGrade.optional(),
  targetSuccess: z.number().min(0.5).max(0.95).optional(),
});

// ─────────────────────────────────────────────────────────────────────────────
// Модерація банку питань (LLM-пайплайн)
// ─────────────────────────────────────────────────────────────────────────────

export const zReviewQueueItem = z.object({
  id: zUuid,
  skillId: z.string(),
  subject: zSubjectId,
  format: zItemFormat,
  prompt: z.string(),
  choices: z.array(z.object({ id: z.string(), text: z.string() })).optional(),
  answerText: z.string(),
  explanation: z.string(),
  hints: z.array(z.string()),
  difficulty: z.number(),
  /** Результати автоматичних перевірок — батько бачить, що вже перевірено. */
  validations: z.array(
    z.object({
      check: z.string(),
      passed: z.boolean(),
      detail: z.string().optional(),
    }),
  ),
  /** Найсхожіше питання в банку — щоб ловити дублікати очима. */
  nearestDuplicate: z.object({ prompt: z.string(), similarity: z.number() }).nullable(),
  createdAt: zIsoDate,
});

export const zReviewDecision = z.object({
  itemId: zUuid,
  decision: z.enum(['approve', 'reject', 'edit']),
  /** Для decision='edit' — виправлена версія. */
  patch: z
    .object({
      prompt: z.string().optional(),
      choices: z.array(z.object({ id: z.string(), text: z.string() })).optional(),
      explanation: z.string().optional(),
      difficulty: z.number().optional(),
    })
    .optional(),
  reason: z.string().max(500).optional(),
});

// ─────────────────────────────────────────────────────────────────────────────
// Стандартна форма помилки
// ─────────────────────────────────────────────────────────────────────────────

export const zApiError = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    /** Деталі валідації, якщо є. */
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof zApiError>;

export const API_ERROR_CODES = {
  UNAUTHORIZED: 'unauthorized',
  /**
   * Секрет не збігся під час ВХОДУ. Окремий код від `unauthorized` навмисно:
   * «ти ввів не той PIN» і «твоя сесія протухла» — це різні події, і клієнт
   * мусить казати про них різне. Раніше обидві приходили як `unauthorized`,
   * і на невірний PIN екран писав «Потрібно зайти ще раз» — тобто брехав.
   *
   * Сам текст лишається однаковим для «немає такого акаунта» і «невірний
   * секрет»: за різницею між ними можна було б перебором дізнатись, які
   * акаунти існують.
   */
  LOGIN_FAILED: 'login_failed',
  FORBIDDEN: 'forbidden',
  /**
   * Такого питання в цій сесії не видавали.
   *
   * Окремий код від `forbidden` — і це не косметика. Обидві біди раніше
   * приїжджали як `forbidden`, а клієнт саме за цим кодом ПАРКУЄ пачку до
   * входу власника (найчастіша причина — за планшет сів хтось інший).
   * Щойно вирок став поштучним, той самий код почав би означати різне:
   * «підміна itemId» (назавжди) і «відповіді іншої дитини» (мине саме).
   * Тому підміна itemId має тепер власний код, а `forbidden` лишається
   * висловлюванням про ВЕСЬ запит: «це не твої відповіді».
   */
  ITEM_NOT_SERVED: 'item_not_served',
  NOT_FOUND: 'not_found',
  VALIDATION: 'validation_failed',
  RATE_LIMITED: 'rate_limited',
  SCHEDULE_BLOCKED: 'schedule_blocked',
  /**
   * Обовʼязкова перерва після відрізка гри. Окремий код від
   * `schedule_blocked`: «зараз ніч» і «ти щойно грав пів години» — це різні
   * події, і дитині треба показати різні екрани. При цій помилці сервер
   * додає `details.resumeAt` — момент, коли перерва закінчиться.
   */
  BREAK_REQUIRED: 'break_required',
  DAILY_LIMIT: 'daily_limit_reached',
  BELOW_MINIMUM: 'below_minimum',
  INSUFFICIENT_FUNDS: 'insufficient_funds',
  SESSION_CLOSED: 'session_closed',
  CONFLICT: 'conflict',
  INTERNAL: 'internal_error',
} as const;
